import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import jaCommon from "@/locales/ja-JP/common.json";
import enCommon from "@/locales/en-US/common.json";
import zhCnCommon from "@/locales/zh-CN/common.json";
import zhTwCommon from "@/locales/zh-TW/common.json";
import koCommon from "@/locales/ko-KR/common.json";

/**
 * Tone (敬語) levels — see docs/chatwork_killer/10_ux_design_localization.md §7.3.
 *
 *   - "polite" (default, 丁寧): 「送信します」          (a.k.a. "formal")
 *   - "casual"        (常体): 「送信」
 *   - "honorific"     (尊敬): 「送信いたします」
 *
 * The internal canonical name `polite` is kept for backward compat with
 * existing tests / consumers; the design doc and product spec sometimes
 * call this `formal`. They are synonyms.
 */
export type Tone = "polite" | "casual" | "honorific";

/** Public list of supported tones. Same shape across all locales. */
export const TONES: readonly Tone[] = ["polite", "casual", "honorific"] as const;

/** Public list of supported locales — keep in sync with `resources` below. */
export const SUPPORTED_LOCALES = [
  "ja-JP",
  "en-US",
  "zh-CN",
  "zh-TW",
  "ko-KR",
] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const TONE_KEY = "ailechat:tone";
const FONT_SCALE_KEY = "ailechat:fontScale";

export function getTone(): Tone {
  if (typeof localStorage === "undefined") return "polite";
  const v = localStorage.getItem(TONE_KEY);
  if (v === "casual" || v === "honorific" || v === "polite") return v;
  return "polite";
}

export function setTone(tone: Tone): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(TONE_KEY, tone);
  // Re-emit so subscribers can react.
  window.dispatchEvent(new CustomEvent("ailechat:tone-changed", { detail: tone }));
}

export type FontScale = "100" | "125" | "150";

export function getFontScale(): FontScale {
  if (typeof localStorage === "undefined") return "100";
  const v = localStorage.getItem(FONT_SCALE_KEY);
  if (v === "125" || v === "150" || v === "100") return v;
  return "100";
}

export function setFontScale(scale: FontScale): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(FONT_SCALE_KEY, scale);
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-font-scale", scale);
  }
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      "ja-JP": { common: jaCommon },
      "en-US": { common: enCommon },
      "zh-CN": { common: zhCnCommon },
      "zh-TW": { common: zhTwCommon },
      "ko-KR": { common: koCommon },
      // legacy short codes (resolve to the long code dictionaries)
      ja: { common: jaCommon },
      en: { common: enCommon },
      zh: { common: zhCnCommon },
      ko: { common: koCommon },
    },
    fallbackLng: "ja-JP",
    supportedLngs: [
      "ja-JP",
      "en-US",
      "zh-CN",
      "zh-TW",
      "ko-KR",
      "ja",
      "en",
      "zh",
      "ko",
    ],
    defaultNS: "common",
    ns: ["common"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "ailechat:lang",
    },
    returnNull: false,
  });

export default i18n;

/**
 * Resolve a key for the current tone. Falls back through:
 *
 *   exact: `${key}.${tone}`
 *   fallback: `${key}.polite`
 *   absolute: `${key}` (no tone)
 *
 * @example useToneT()("compose.send")  // -> "送信します"
 */
export function tonalKeys(key: string, tone: Tone = getTone()): string[] {
  return [`${key}.${tone}`, `${key}.polite`, key];
}
